/**
 * Shared module-scope types + small pure helpers for the `team/`
 * route folder.
 *
 * Extracted verbatim from `routes/TeamPage.tsx` during the
 * behaviour-preserving decomposition (refs #840). The team page used
 * to declare ~18 components in a single 2.5k-LOC file; the schema-
 * derived row types (`Team`/`Cycle`/`MemberRow`/…) and the cross-
 * component helpers (`readFreePlanCapLimit`, `clampFte`, the horizon
 * date math) were module-scope locals shared by several of those
 * components. Lifting them here lets each component live in its own
 * file without re-deriving the types or duplicating the helpers.
 *
 * `TeamPage.tsx` re-exports the symbols its existing importers expect
 * (the `TEAM_*_KEY` cache keys + the named subcomponents that
 * `MembersSidebar` imports), so this split is invisible to App.tsx,
 * MembersSidebar, and the vitest suite.
 */
import { ApiError } from "../../api/client";
import type { components, paths } from "../../api/schema.gen";

export type Team =
  paths["/api/teams/{team_id}"]["get"]["responses"]["200"]["content"]["application/json"];
export type Cycle =
  paths["/api/teams/{team_id}/cycles"]["get"]["responses"]["200"]["content"]["application/json"][number];
export type CycleCreateBody =
  paths["/api/teams/{team_id}/cycles"]["post"]["requestBody"]["content"]["application/json"];
export type MemberRow = components["schemas"]["MemberRow"];
export type MemberPatchBody = components["schemas"]["MemberPatchBody"];
// Issue #440 — depart response is wrapped `{membership, future_assignments}`
// per Option A (founder-locked 2026-05-16). The SPA reads
// `future_assignments` to render a calm "review and reassign" banner
// after a successful depart so the lead can manually reassign any
// shifts the departed member still held past their effective date.
export type DepartMembershipResponse = components["schemas"]["DepartMembershipResponse"];
export type FutureAssignmentCycleSummary = components["schemas"]["FutureAssignmentCycleSummary"];
export type InviteRow = components["schemas"]["InviteRow"];
export type InviteCreateBody = components["schemas"]["InviteCreateBody"];
// Issue #439 — structured departure-reason enum (replaces pre-#439
// free-text). The dropdown picker in `<MarkAsLeftTeamDialog/>` renders
// neutral display copy mapped to each enum key.
export type DepartureReason = components["schemas"]["DepartureReason"];
export const DEPARTURE_REASON_OPTIONS: ReadonlyArray<{ value: DepartureReason; label: string }> = [
  // Display copy chosen for neutrality (per founder direction 2026-05-16
  // + brand-pillar "factual + neutral" guidance from issue #439 body):
  //   * "Resigned" — member-initiated leave.
  //   * "Contract ended" — natural end of a fixed-term contract.
  //   * "Reassigned" — moved to a different team (legacy free-text was
  //     "moved to other team"; this maps onto the same intent).
  //   * "Other arrangement" — calm fallback that doesn't editorialise.
  //   * "Terminated" — kept verbatim because softening ("Let go" /
  //     "Released") reads more judgemental than the bare verb.
  { value: "resigned", label: "Resigned" },
  { value: "end_of_contract", label: "Contract ended" },
  { value: "reassigned", label: "Reassigned" },
  { value: "terminated", label: "Terminated" },
  { value: "other", label: "Other arrangement" },
] as const;
// Brief 31 / issue 008 — manual-add body type for the
// `POST /api/teams/{tid}/members/manual` endpoint shipped alongside
// the SPA's "Add manually" path on the Members tab.
export type MemberManualCreateBody = components["schemas"]["MemberManualCreateBody"];
export type TeamConfig = components["schemas"]["TeamConfig"];
export type TeamAttribute = components["schemas"]["TeamAttribute"];

export const WEEKDAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

// Wave-6F (#479) — legacy tab URL params we strip-and-redirect on
// mount. The pre-6F URL semantics put `?tab=statistics|cycles|members`
// on the team-page; the new home view stacks Statistics + Cycles in
// the main content area with Members in the LEFT sidebar, so the
// param has no surface to select. The `workload` value is the
// pre-6A wave-5B alias kept around in 6A's redirect.
export const LEGACY_TAB_VALUES = ["statistics", "cycles", "members", "workload"] as const;

// Wave-6F (#479) — exported so the new `<MembersSidebar/>` shares the
// exact same cache keys as the legacy MembersTab. React Query dedupes
// on key equality, so co-mounting both surfaces during a transition
// (the smoke / vitest tests still drive `?tab=members` URLs) means a
// single network roundtrip serves both.
export const TEAM_MEMBERS_KEY = (teamId: string) => ["team-members", teamId] as const;
export const TEAM_INVITES_KEY = (teamId: string) => ["team-invites", teamId] as const;
/** #1900 — the team's tags in use (`GET /members/tags`), for the filter + suggestions. */
export const TEAM_MEMBER_TAGS_KEY = (teamId: string) => ["team-member-tags", teamId] as const;

// Crew-composition floor plan (`docs/design/crew-composition-floor-constraint-plan.md`)
// §4.2 + §7 Chunk 3 — the Members tab consumes the team's attribute
// catalogue from the Chunk 1 endpoint to render the per-member chip
// selector. Cache key is shared with the future Attributes management
// page (Chunk 5) so add/rename/delete there auto-refreshes the
// selector here.
export const TEAM_ATTRIBUTES_KEY = (teamId: string) => ["team-attributes", teamId] as const;

// Issue #462 — extract the free-plan cap envelope from a 402 ApiError.
//
// Both `POST /api/teams/:tid/members/manual` and `POST /api/teams/:tid/invites`
// raise `HTTPException(status_code=402, detail={"upgrade_required": True,
// "limit": N, ...})` when the free-plan member cap is hit (see
// `backend/src/api/routes/members.py` — both call sites use the same
// envelope). The canonical error middleware wraps that dict under
// `body.error.detail` (`backend/src/api/middleware/errors.py`), but
// raw FastAPI shapes can surface as `body.detail`. We read both
// defensively — mirroring `test_members_and_invites.py` which does the
// same. Returns the cap limit (N) when 402 + `upgrade_required: true`
// holds, otherwise null. Callers render the calm member-limit message
// below instead of the generic "Request failed (402)" string the
// default `ApiError.detail` getter falls back to when the wire body's
// `detail` is a dict (not a string).
export function readFreePlanCapLimit(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null;
  const body = err.body as Record<string, unknown> | null;
  if (body == null || typeof body !== "object") return null;
  const candidates: unknown[] = [];
  const envelope = (body as { error?: unknown }).error;
  if (envelope && typeof envelope === "object") {
    candidates.push((envelope as { detail?: unknown }).detail);
  }
  candidates.push((body as { detail?: unknown }).detail);
  for (const c of candidates) {
    if (c && typeof c === "object") {
      const d = c as { upgrade_required?: unknown; limit?: unknown };
      if (d.upgrade_required === true) {
        return typeof d.limit === "number" ? d.limit : 5;
      }
    }
  }
  return null;
}

// Issue #462 + #1294 — calm cap copy used by both the manual-add and
// invite onError handlers. Factual limit statement only: the Billing
// surface was removed (#1294), so the copy states the limit and the
// dialogs' sibling CTA row says "Contact us to raise it" without
// linking anywhere. No tier/pricing mechanics are promised, per
// `feedback_per_component_toggling_over_tiers.md`.
export function freePlanCapMessage(limit: number): string {
  return `This workspace has reached its member limit (${limit} active members).`;
}
